import { useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { usePost } from '../../API';
import { UserInfoContext } from '../../Context';
import { SupportRequest } from '../../SupportUtil';

/**
 * Custom hook for support case actions (create, claim, navigate)
 * Encapsulates the logic from Support.tsx for creating and managing support cases
 */
export const useSupportCaseActions = (refetch?: () => Promise<any>) => {
  const history = useHistory();
  const createSupportCaseAPI = usePost('/support/create_case');
  const reassignSupportCaseAPI = usePost('/support/reassign_case');
  const user = useContext(UserInfoContext);

  /**
   * Get or create a support case
   * @param caseRequest - The support request to create/claim
   * @param leaveUnassigned - If true, creates case without claiming it
   * @returns The support case UID
   */
  const getOrCreateSupportCase = useCallback(
    async (caseRequest: SupportRequest, leaveUnassigned?: boolean): Promise<string> => {
      let supportCase = caseRequest.row.support_case;

      // Case 1: Support case already exists
      if (supportCase) {
        // Only attempt reassignment if:
        // 1. We're not leaving it unassigned (i.e. user is claiming the case)
        // 2. Current assignee is "system" (indicating unclaimed case)
        if (!leaveUnassigned && caseRequest.row.assigned_to?.toLowerCase() === 'system') {
          const result = await reassignSupportCaseAPI({
            caseId: supportCase,
            reassignTo: user.uid!,
            failIfAssigned: true
          });

          // If reassignment fails, someone else claimed it - that's ok, we'll still navigate
          if ('error' in result) {
            console.log('Case was already claimed by someone else');
          }
        }
      // Case 2: No known existing support case - create a new one
      } else {
        const result = await createSupportCaseAPI({
          leaveUnassigned,
          channel: 'default',
          ...(caseRequest.row.source ? { contact: caseRequest.row.source } : {})
        });

        if (result.supportCase) {
          supportCase = result.supportCase.uid;
        }

        if (!supportCase) {
          throw new Error('unable to create support case at this time');
        }
      }

      return supportCase;
    },
    [createSupportCaseAPI, reassignSupportCaseAPI, user.uid]
  );

  /**
   * Handle claiming a case
   * Creates/claims the case, refetches the support cases list, and navigates to detail page
   */
  const handleClaimCase = useCallback(
    async (caseRequest: SupportRequest) => {
      try {
        const supportCaseId = await getOrCreateSupportCase(caseRequest, false);
        // Refetch to get updated data
        if (refetch) {
          await refetch();
        }
        // Navigate to case detail page using the support_case ID
        history.push(`/admin-panel/support/${supportCaseId}`);
      } catch (error) {
        console.error('Error claiming case:', error);
      }
    },
    [getOrCreateSupportCase, refetch, history]
  );

  /**
   * Handle clicking on a case to view it
   * Creates the case (leave unassigned) and navigates to detail page
   */
  const handleCaseClick = useCallback(
    async (caseRequest: SupportRequest) => {
      try {
        // Create case (leave unassigned) and get the support_case ID
        const supportCaseId = await getOrCreateSupportCase(caseRequest, true);
        // Navigate to case detail page using the support_case ID
        history.push(`/admin-panel/support/${supportCaseId}`);
      } catch (error) {
        console.error('Error opening case:', error);
      }
    },
    [getOrCreateSupportCase, history]
  );

  return {
    getOrCreateSupportCase,
    handleClaimCase,
    handleCaseClick
  };
};
